import { useForm, useSelector } from '@tanstack/react-form';
import { skipToken, useQuery } from '@tanstack/react-query';
import { FORM_MARKER } from '@tronche2tek/shared-types';
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getCardTemplateSvgs } from '../../api/cards';
import {
  iseeControllerGetFields,
  luccaControllerGetFields,
} from '../../api/generated';
import { useIseeEnabled } from '../../api/isee';
import { useLuccaEnabled } from '../../api/lucca';
import { LoadingSpinner } from './shared';
import type { ExternalSource, FieldMode } from './template-defaults';
import {
  boundField,
  configurableVars,
  fieldsFor,
  incompleteIn,
  keepsValue,
  seedFields,
  sourceOf,
} from './template-defaults';
import type { CardTemplate } from './types';

interface ExternalSourceSpec {
  prefix: string;
  enabled: boolean;
  fetch: typeof iseeControllerGetFields | typeof luccaControllerGetFields;
}

/** Which external source applies is decided by the template's type. */
function externalSourceFor(
  type: CardTemplate['type'],
  iseeEnabled: boolean,
  luccaEnabled: boolean,
): ExternalSourceSpec | null {
  if (type === 'student') {
    return {
      prefix: 'isee',
      enabled: iseeEnabled,
      fetch: iseeControllerGetFields,
    };
  }
  if (type === 'employee') {
    return {
      prefix: 'lucca',
      enabled: luccaEnabled,
      fetch: luccaControllerGetFields,
    };
  }
  return null;
}

interface TemplateDefaultsPanelProps {
  template: CardTemplate;
  onSave: (defaults: Record<string, string>) => void;
}

/**
 * Per-template "Default values" editor. Every Liquid variable the template uses
 * — bar `upn` and `photo`, which the renderer injects — is listed here and has
 * to be given a source: a fixed string, an Entra attribute, an external field
 * (ISEE for student templates, Lucca for employee ones), or a value filled at
 * generation time. Source-linked ones are stored as `<source>:<field>` and
 * resolved per recipient.
 *
 * Entra is offered whatever the template type: the recipient's Entra account is
 * looked up for every card regardless of where the batch comes from.
 *
 * This component fetches; {@link DefaultsForm} below owns the editing.
 */
export function TemplateDefaultsPanel({
  template,
  onSave,
}: TemplateDefaultsPanelProps) {
  const { t } = useTranslation();
  const iseeEnabled = useIseeEnabled();
  const luccaEnabled = useLuccaEnabled();

  const source = useMemo(
    () => externalSourceFor(template.type, iseeEnabled, luccaEnabled),
    [template.type, iseeEnabled, luccaEnabled],
  );

  const { data: fieldsData } = useQuery({
    queryKey: ['external-fields', source?.prefix],
    // `skipToken` narrows for us: the fetch only exists when the source does.
    queryFn:
      source && source.enabled
        ? async () => {
            const { data } = await source.fetch({ throwOnError: true });
            return data;
          }
        : skipToken,
    retry: false,
    staleTime: Infinity,
  });

  // Stable identities: both go into every DefaultRow, and `configurableVars`
  // re-runs regex extraction over both SVGs when not memoised.
  const external: ExternalSource | null = useMemo(
    () =>
      source
        ? {
            prefix: source.prefix,
            fields: source.enabled ? (fieldsData?.fields ?? []) : [],
          }
        : null,
    [source, fieldsData],
  );

  // The configurable variables come from the template's own SVGs. react-query
  // owns the request, so a template switched mid-flight cannot land late over
  // the new one. A failure leaves `vars` empty, which reads below as "no
  // configurable variables".
  const { data: svgs, isPending: loading } = useQuery({
    queryKey: ['admin', 'template-svgs', template.id] as const,
    queryFn: () => getCardTemplateSvgs(template.id),
    retry: false,
    staleTime: Infinity,
  });
  const vars = useMemo(() => (svgs ? configurableVars(svgs) : []), [svgs]);

  return (
    <div className="space-y-3 border-t border-t-rule bg-blue/4 px-4 py-3">
      <p className="text-xs text-muted">{t('admin.templates.defaultsHelp')}</p>
      <p className="text-xs text-muted-soft">
        {t('admin.templates.defaultsExternalHint')}
      </p>
      {loading ? (
        <LoadingSpinner />
      ) : vars.length === 0 ? (
        <p className="text-xs text-muted-soft italic">
          {t('admin.templates.noDefaultsVars')}
        </p>
      ) : (
        // Mounted only once the variables are known, so the stored defaults are
        // the form's own `defaultValues` — nothing has to seed it afterwards.
        // Keyed by template so switching one starts a fresh form.
        <DefaultsForm
          key={template.id}
          vars={vars}
          defaults={template.defaults}
          external={external}
          onSave={onSave}
        />
      )}
    </div>
  );
}

interface DefaultsFormProps {
  vars: string[];
  defaults: Record<string, string> | null | undefined;
  external: ExternalSource | null;
  onSave: (defaults: Record<string, string>) => void;
}

function useDefaultsForm({
  vars,
  defaults,
  external,
  onSave,
}: DefaultsFormProps) {
  return useForm({
    defaultValues: seedFields(vars, defaults, external),
    onSubmit: ({ value }) => {
      if (incompleteIn(vars, value, external).length > 0) {
        return;
      }
      // Strip empty values so we don't persist empty strings (`form:` and
      // external markers are non-empty, so they're kept).
      onSave(
        Object.fromEntries(
          Object.entries(value.values).filter(([, v]) => v.trim() !== ''),
        ),
      );
    },
  });
}

type DefaultsFormApi = ReturnType<typeof useDefaultsForm>;

/**
 * The editable grid, one row per template variable. Its props are fixed for its
 * keyed lifetime, so `onSubmit` may close over them.
 *
 * Nothing here subscribes to the form: each row reads its own two fields, and
 * the completeness check sits behind `form.Subscribe`. A keystroke therefore
 * redraws one row and the footer, not the whole grid.
 */
function DefaultsForm(props: DefaultsFormProps) {
  const { t } = useTranslation();
  const { vars, external } = props;
  const form = useDefaultsForm(props);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      className="space-y-3"
    >
      <div className="grid grid-cols-2 gap-3">
        {vars.map((v) => (
          <DefaultRow key={v} form={form} name={v} external={external} />
        ))}
      </div>
      {/* Joined to a string: a selector returning a fresh array would fail
          the store's `===` compare and re-render this on every keystroke. */}
      <form.Subscribe
        selector={(state) =>
          incompleteIn(vars, state.values, external).join(', ')
        }
      >
        {(incomplete) => (
          <>
            {incomplete !== '' && (
              <p className="text-xs text-magenta">
                {t('admin.templates.emptyFixedError', { fields: incomplete })}
              </p>
            )}
            <button
              type="submit"
              disabled={incomplete !== ''}
              className="btn-blue disabled:cursor-not-allowed disabled:opacity-40"
            >
              {t('admin.templates.saveDefaults')}
            </button>
          </>
        )}
      </form.Subscribe>
    </form>
  );
}

/** One template variable: its name, its mode picker and its value input. */
function DefaultRow({
  form,
  name,
  external,
}: {
  form: DefaultsFormApi;
  name: string;
  external: ExternalSource | null;
}) {
  const { t } = useTranslation();
  const valueId = useId();
  const mode = useSelector(
    form.store,
    (state) => state.values.modes[name] ?? 'fixed',
  );
  const value = useSelector(
    form.store,
    (state) => state.values.values[name] ?? '',
  );
  const hasExternalFields = (external?.fields.length ?? 0) > 0;

  // Switch this default between a fixed value, an Entra attribute, an external
  // field and a fill-at-generation form field, dropping the stored value when
  // it no longer applies so a stale marker or string doesn't linger.
  const setMode = (next: FieldMode) => {
    form.setFieldValue(`modes.${name}`, next);
    if (next === 'form') {
      form.setFieldValue(`values.${name}`, FORM_MARKER);
      return;
    }
    if (!keepsValue(next, value, external)) {
      form.setFieldValue(`values.${name}`, '');
    }
  };

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label htmlFor={valueId} className="mono text-xs text-muted">
          {name}
        </label>
        <ModeToggle
          active={mode}
          offerExternal={hasExternalFields}
          externalLabel={
            external ? t(`admin.templates.defaultsMode.${external.prefix}`) : ''
          }
          onPick={setMode}
        />
      </div>
      {mode === 'form' ? (
        <input
          id={valueId}
          type="text"
          disabled
          value=""
          placeholder={t('admin.templates.formFieldPlaceholder')}
          className="w-full px-2 py-1.5 text-xs opacity-60"
        />
      ) : mode === 'entra' || (mode === 'external' && hasExternalFields) ? (
        // The <select> offers bare field names while the form holds the
        // `<source>:<field>` marker, so it is bound by hand.
        <select
          id={valueId}
          value={boundField(mode, value, external)}
          onChange={(e) =>
            form.setFieldValue(
              `values.${name}`,
              e.target.value
                ? `${sourceOf(mode, external)}:${e.target.value}`
                : '',
            )
          }
          className="w-full px-2 py-1.5 text-xs"
        >
          <option value="">
            {t('admin.templates.externalFieldPlaceholder')}
          </option>
          {fieldsFor(mode, external).map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      ) : (
        <form.Field name={`values.${name}`}>
          {(field) => (
            <input
              id={valueId}
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              className="w-full px-2 py-1.5 text-xs"
            />
          )}
        </form.Field>
      )}
    </div>
  );
}

/** The four source pickers above a value input. */
function ModeToggle({
  active,
  offerExternal,
  externalLabel,
  onPick,
}: {
  active: FieldMode;
  offerExternal: boolean;
  externalLabel: string;
  onPick: (mode: FieldMode) => void;
}) {
  const { t } = useTranslation();
  const labels: Record<FieldMode, string> = {
    fixed: t('admin.templates.defaultsMode.fixed'),
    entra: t('admin.templates.defaultsMode.entra'),
    external: externalLabel,
    form: t('admin.templates.defaultsMode.form'),
  };
  const offered: FieldMode[] = [
    'fixed',
    'entra',
    ...(offerExternal ? (['external'] as const) : []),
    'form',
  ];

  return (
    <div className="flex gap-0.5">
      {offered.map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onPick(m)}
          className="px-1.5 py-0.5 text-[10px] font-medium transition-colors"
          style={
            active === m
              ? { background: 'var(--card)', color: 'var(--ink)' }
              : { background: 'transparent', color: 'var(--muted)' }
          }
        >
          {labels[m]}
        </button>
      ))}
    </div>
  );
}
